<template>
  <div class="loader" v-if="isLoading"></div>
  <div v-else class="page">
    <div class="loader loader--overlay" v-if="isChangeTab"></div>

    <div>
      <img class="icon icon--link icon--bottom" src="~/images/arrow-left.svg" v-svg-inline>
      <router-link :to="backRoute()">Вернуться назад</router-link>
    </div>

    <h1 class="page-header__title">
      {{ campaign.name }}
    </h1>

    <div class="page__header-subtitle" v-html="subtitle"></div>

    <tab-filter
      :items="badges"
      :active-item="activeBadge"
      :onChange="onSelectBadge"
    />

    <div>
      <span class="page__header-places">{{ contest_group.places }}</span>
      {{ placesLabel() }}

      <span v-if="showMainPlacesQuota() && contest_group.budget_places_with_free_quota">
        | <span class="page__header-free-quota">{{ contest_group.budget_places_with_free_quota }}</span>
        {{ getBudgetPlacesLabel(contest_group.budget_places_with_free_quota) }} с учетом свободных квотных мест
      </span>
    </div>

    <div class="card card--banner" v-show="this.showMainPlacesBanner()">
      <img class="icon icon--primary" src="~images/info.svg" v-svg-inline/>
      <p>
        Уважаемые абитуриенты!<br>
        Обратите внимание, что <strong>после 3 августа 2026 года</strong> — по итогам зачисления на места в рамках
        отдельных, особых и целевых квот — количество бюджетных мест по основному конкурсу
        <strong>может быть увеличено</strong>. Это произойдет, если останутся вакантные места, не занятые по квотам.
        В таком случае <strong>проходной балл может снизиться</strong><br>
        Рекомендуем следить за актуальной информацией на нашем сайте.
      </p>
    </div>

    <div class="join" v-show="isPublished()">
      <button
        class="btn btn--xs btn--primary"
        :class="{ 'btn--secondary': exclusiveFilter === 'is_highest_passing_priority' }"
        type="button"
        @click="CHANGE_EXCLUSIVE_FILTER('is_highest_passing_priority')"
      >
        <img class="icon icon--none" src="~/images/filter.svg" v-svg-inline>
        <span
          class="hint hint--top hint--md"
          :data-hint="hintForExclusiveFilter('is_highest_passing_priority')"
        >
          По высшему проходному приоритету
        </span>
      </button>
      <button
        class="btn btn--xs btn--primary"
        :class="{ 'btn--secondary': exclusiveFilter === 'is_highest_priority' }"
        type="button"
        @click="CHANGE_EXCLUSIVE_FILTER('is_highest_priority')"
      >
        <img class="icon icon--none" src="~/images/filter.svg" v-svg-inline>
        <span
          class="hint hint--top hint--md"
          :data-hint="hintForExclusiveFilter('is_highest_priority')"
        >
          По основному высшему приоритету
        </span>
      </button>
      <button
        class="btn btn--xs btn--primary"
        :class="{ 'btn--secondary': exclusiveFilter === 'priority' }"
        type="button"
        @click="CHANGE_EXCLUSIVE_FILTER('priority')"
      >
        <img class="icon icon--none" src="~/images/filter.svg" v-svg-inline>
        <span
          class="hint hint--top hint--md"
          :data-hint="hintForExclusiveFilter('priority')"
        >
          По первому приоритету
        </span>
      </button>
    </div>
    <div class="join" v-show="!isPublished()">
      <button
        class="btn btn--xs btn--primary"
        :class="{ 'btn--secondary': activeFilters.includes('has_agreement') }"
        type="button"
        v-if="isBudget()"
        @click="CHANGE_ACTIVE_FILTERS('has_agreement')"
      >
        С учетом согласий
      </button>
      <button
        class="btn btn--xs btn--primary"
        :class="{ 'btn--secondary': activeFilters.includes('has_contract') }"
        type="button"
        v-if="isPay()"
        @click="CHANGE_ACTIVE_FILTERS('has_contract')"
      >
        С учетом договоров
      </button>
      <button
        class="btn btn--xs btn--primary"
        :class="{ 'btn--secondary': activeFilters.includes('priority') }"
        type="button"
        @click="CHANGE_ACTIVE_FILTERS('priority')"
      >
        По первому приоритету
      </button>
    </div>

    <div>
      <div class="page__generated">Данные на <b>{{ formattedDate }}</b></div>
      <div class="page__time-zone">Россия, Томск (UTC +7)</div>
      <component :is="resolveComponent()"></component>
      <div v-if="contest_group.abiturients.length === 0">Нет абитуриентов в данной конкурсной группе</div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import Bachelor from './_Bachelor.vue'
import Magistrant from './_Magistrant.vue'
import Aspirant from './_Aspirant.vue'
import GroupsMixin from '@main/mixins/groups'
import tabFilter from '@main/components/TabFilter.vue'

export default {
  components: {
    Bachelor,
    Magistrant,
    Aspirant,
    tabFilter,
  },
  mixins: [GroupsMixin],
  data: () => ({
    isLoading: false,
    isChangeTab: false
  }),
  computed: {
    formattedDate() {
      return this.$dayjs(this.generated_at, 'DD.MM.YYYY HH:mm:ss').format('D MMMM YYYY HH:mm')
    },
    subtitle() {
      const nameDetails = this.isPublished()
        ? 'Конкурсный список по направлению'
        : 'Список лиц, подавших документы на направление подготовки';
      const commonDetails = `, всего бюджетных мест: <b>${this.contest_group.budget_places}</b>, платных мест: <b>${this.contest_group.pay_places}</b>`

      switch (this.$route.params.education_level) {
        case 'bachelor':
        case 'aspirant':
          return `${nameDetails} <b>${this.contest_group.direction}</b>${commonDetails}`
        case 'magistrant':
          return `${nameDetails} <b>${this.contest_group.profiles.join(', ')}</b>${commonDetails}`
      }
    }
  },
  async created() {
    this.isLoading = true

    const params = {
      education_level: this.$route.params.education_level,
      edu_form: this.$route.params.edu_form,
      contest_group_id: this.$route.params.contest_group_id
    }
    await this.getContestGroup(params)
    this.isLoading = false
  },
  beforeRouteLeave(to, from, next) {
    this.CLEAR_FILTERS()
    next()
  },
  methods: {
    ...mapMutations('contestGroup', ['CLEAR_FILTERS']),
    resolveComponent() {
      const level = this.$route.params.education_level
      return level.replace(/_additional$/, '')
    },
    getPluralForm(number, titles) {
      const cases = [2, 0, 1, 1, 1, 2];
      return titles[
        number % 100 > 4 && number % 100 < 20
          ? 2
          : cases[number % 10 < 5 ? number % 10 : 5]
      ]
    },
    getBudgetPlacesLabel(places) {
      return this.getPluralForm(places, ['бюджетное место', 'бюджетных места', 'бюджетных мест']);
    },
    placesLabel() {
      const places = this.contest_group.places;
      const isPaid = this.activeBadge.text.includes('Платные места');
      return this.getPluralForm(places,
        isPaid
          ? ['платное место', 'платных места', 'платных мест']
          : ['бюджетное место', 'бюджетных места', 'бюджетных мест']
      );
    },
    async onSelectBadge(item) {
      if (item.value === this.$route.params.contest_group_id) return
      this.isChangeTab = true
      this.CLEAR_FILTERS()
      this.$router.push({ params: { contest_group_id: item.value } })

      await this.getContestGroup({
        education_level: this.$route.params.education_level,
        edu_form: this.$route.params.edu_form,
        contest_group_id: item.value
      })

      this.isChangeTab = false
    },
    backRoute() {
      const { education_level, edu_form } = this.$route.params
      return {
        name: 'education_level_index',
        params: { education_level, edu_form }
      }
    }
  }
}
</script>